import { useCallback, useEffect, useMemo, useState } from "react";
import { qtyError, trimQty } from "./fulfilHelpers";

/**
 * Tick boxes and typed quantities for one step's lines. Every line starts
 * ticked with its suggested quantity; both reset when the lines reload.
 *
 * @param {!Array<!Object>} rows
 * @param {{
 *   idKey: string,
 *   defaultQty?: function(!Object): *,
 *   maxQty?: function(!Object): *,
 *   qtyError?: function(string, *): ?string,
 * }} options Without `defaultQty` the step has no quantity column.
 * @return {{
 *   checked: !Set<*>,
 *   quantities: !Object<string, string>,
 *   toggle: function(*),
 *   toggleAll: function(boolean),
 *   setSelected: function(!Array<*>),
 *   setQty: function(*, string),
 *   errorFor: function(!Object): ?string,
 *   selectedRows: !Array<!Object>,
 *   hasErrors: boolean,
 * }}
 */
export const useStepSelection = (rows, { idKey, defaultQty, maxQty, qtyError: qtyErrorFn }) => {
  const validateQty = qtyErrorFn || qtyError;
  const rowsKey = rows.map(row => row[idKey]).join(",");
  const [checked, setChecked] = useState(() => new Set());
  const [quantities, setQuantities] = useState({});

  useEffect(() => {
    setChecked(new Set(rows.map(row => row[idKey])));
    setQuantities(
      defaultQty ? Object.fromEntries(rows.map(row => [row[idKey], trimQty(defaultQty(row))])) : {},
    );
    // Reset only when a different set of lines arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsKey]);

  const toggle = useCallback(id => {
    setChecked(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setSelected = useCallback(ids => setChecked(new Set(ids)), []);

  const toggleAll = useCallback(
    on => setChecked(on ? new Set(rows.map(row => row[idKey])) : new Set()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowsKey],
  );

  const setQty = useCallback(
    (id, value) => setQuantities(previous => ({ ...previous, [id]: value })),
    [],
  );

  const errorFor = useCallback(
    row =>
      defaultQty && checked.has(row[idKey])
        ? validateQty(quantities[row[idKey]], maxQty ? maxQty(row) : undefined)
        : null,
    [checked, defaultQty, idKey, maxQty, quantities, validateQty],
  );

  const selectedRows = useMemo(
    () => rows.filter(row => checked.has(row[idKey])),
    [rows, checked, idKey],
  );
  const hasErrors = selectedRows.some(row => errorFor(row));

  return {
    checked,
    quantities,
    toggle,
    toggleAll,
    setSelected,
    setQty,
    errorFor,
    selectedRows,
    hasErrors,
  };
};
